import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import { MessageCircle, Check, Loader2, X } from "lucide-react";

/**
 * V179 — Admin toggle for the public-landing AI chatbot.
 */
export default function AdminChatbotToggle() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const adminToken = (typeof window !== "undefined" && localStorage.getItem("admin_token")) || "";

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/agent-os-chat/settings`);
      setEnabled(Boolean(r.data?.landing_enabled));
      setError("");
    } catch (e) {
      setError(t.cat_t_load_fail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (next) => {
    setSaving(true);
    setError("");
    try {
      await axios.post(
        `${API}/agent-os-chat/admin/settings`,
        { landing_enabled: next },
        { headers: { "X-Admin-Token": adminToken } },
      );
      setEnabled(next);
    } catch (e) {
      setError(e?.response?.data?.detail || t.cb_admin_save_fail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6" data-testid="admin-chatbot-toggle">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-violet-600 font-extrabold mb-1">
            <MessageCircle className="w-3 h-3" /> Agent OS Chatbot · V179
          </div>
          <h3 className="text-base font-bold text-gray-900">{t.cb_admin_h}</h3>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">{t.cb_admin_sub}</p>
        </div>
        <div className="flex items-center gap-3">
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : (
            <>
              <span className={`text-xs font-bold uppercase tracking-wider ${enabled ? "text-emerald-600" : "text-gray-400"}`} data-testid="admin-chatbot-status">
                {enabled ? t.cb_admin_on : t.cb_admin_off}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                onClick={() => toggle(!enabled)}
                disabled={saving}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-60 ${enabled ? "bg-emerald-500" : "bg-gray-300"}`}
                data-testid="admin-chatbot-toggle-switch"
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`}
                >
                  {saving ? (
                    <Loader2 className="w-3 h-3 m-1 animate-spin text-gray-500" />
                  ) : enabled ? (
                    <Check className="w-3 h-3 m-1 text-emerald-600" />
                  ) : (
                    <X className="w-3 h-3 m-1 text-gray-500" />
                  )}
                </span>
              </button>
            </>
          )}
        </div>
      </div>
      {error && (
        <div className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
          {error} {!adminToken && "(localStorage.admin_token)"}
        </div>
      )}
    </div>
  );
}
